import React, {useEffect, useState} from "react";
import {Person} from "../../../services/interface/query/person";
import {useTranslation} from "react-i18next";
import {useFindEmploymentHistoryDetails} from "../../../services/apis/apiWorkInformation";
import {PaginationWorkInformation} from "../../../services/interface/query/pagination-work-information";
import {WorkingInformationInterface} from "../../../services/interface/query/working-information-interface";
import NotPermissionComponent from "../NotPermissionComponent";
import CustomTable, {TypeItemTable} from "../../../components/general/CustomTable";
import {getMessageGeneric} from "../../../services/configuration/errorHandle";
import * as IconsMui from "@mui/icons-material";
import {IconButton, Collapse} from "@material-tailwind/react";

interface Props {
    person?: Person;
}

/**
 * Detalle mensual de ingresos laborales con tabla paginada y desplegable.
 * Consume el endpoint POST /income/historical/details.
 *
 * @author cfernandez
 * @version 1.0.0
 * @since 2026-04-08
 */
export default function EmploymentHistoryDetails({person}: Props) {
    const {t} = useTranslation("global") as { t: (key: string) => string };

    const {
        mutate: getDetails,
        data: successDetails,
        error: errorDetails,
        isPending: loadingDetails,
    } = useFindEmploymentHistoryDetails()

    const [list, setList] = useState<PaginationWorkInformation | undefined>()
    const [listElement, setListElement] = useState<TypeItemTable[] | undefined>()
    const [index, setIndex] = useState<number | undefined>()
    const [isOpen, setIsOpen] = React.useState(false);

    useEffect(() => {
        if (person?.identification) {
            getDetails({identifier: person.identification, page: 0, msj: t('monthly-detail')})
        }
    }, [person]);

    useEffect(() => {
        if (successDetails) {
            setList(successDetails)
        }
    }, [successDetails]);

    useEffect(() => {
        if (list && list.content && list.content.length >= 0) {
            setListElement(list.content.length === 0 ? [] :
                list.content?.map((item: WorkingInformationInterface) => ({
                        row: [
                            item.year,
                            item.month,
                            item.taxpayerName,
                            item.occupation,
                            item.salary ? `$${item.salary}` : '-',
                            item.startDate,
                            item.endDate,
                        ]
                    })
                )
            )
        }
    }, [list]);

    useEffect(() => {
        if ((index || index === 0) && person?.identification) {
            getDetails({identifier: person.identification, page: index, msj: t('monthly-detail')})
        }
    }, [index]);

    const header = [
        t('year'),
        t('month'),
        t('emp-name'),
        t('post'),
        t('salary'),
        t('start-date'),
        t('departure-date'),
    ];

    return (
        <div className={'flex flex-col gap-3'}>
            <div className={'flex flex-row gap-3 items-center !border border-transparent border-b-secondary pb-3'}>
                <h3>{t('monthly-detail')}</h3>
                <div className={'ml-auto'}>
                    <IconButton placeholder="icon-button"
                                size="sm"
                                variant="text"
                                onClick={() => setIsOpen((cur) => !cur)}
                    >
                        <IconsMui.KeyboardArrowDownTwoTone className="h-6 w-6"/>
                    </IconButton>
                </div>
            </div>
            <Collapse open={isOpen} className="bg-white">
                <NotPermissionComponent
                    title={'/income/historical/details'}
                    loading={loadingDetails}
                    error={errorDetails}
                    component={
                        <CustomTable
                            pagination={true}
                            error={getMessageGeneric(errorDetails, '/income/historical/details')}
                            loading={loadingDetails}
                            header={header}
                            number={list?.number}
                            totalPages={list?.totalPages}
                            totalElements={list?.totalElements}
                            {...{listElement, setIndex}}
                        />
                    }
                />
            </Collapse>
        </div>
    )
}
